import { Integration } from '@/types/integrations'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import queryKeys from '../query-keys'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import buildService from '../create-service'

const service = buildService<Integration>({
  pathCategory: 'integrations',
})

export function useGetIntegration({
  id,
  ...options
}: UseGetDetailOptions<Integration>) {
  const query = useGetDetail<Integration>({
    id,
    queryKey: queryKeys.integrations.detail({ id, filters: options.filters }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    integration: query.data,
    ...query,
  }
}

export function useCreateIntegration(
  options: UseCreateDetailOptions<Integration> = {},
) {
  const mutation = useCreateDetail<Integration>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createIntegration: mutation.mutateAsync,
    ...mutation,
  }
}

export function useUpdateIntegration(
  options: UseUpdateDetailOptions<Integration> = {},
) {
  const mutation = useUpdateDetail<Integration>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updateIntegration: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteIntegration(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<Integration>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deleteIntegration: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetIntegrations({
  filters,
  ...options
}: UseGetListOptions<Integration> = {}) {
  const query = useGetList<Integration>({
    filters,
    queryKey: queryKeys.integrations.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    integrations: query.allData,
    ...query,
  }
}

export function useUpdateIntegrations(
  options: UseUpdateListOptions<Integration> = {},
) {
  const mutation = useUpdateList<Integration>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updateIntegrations: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeleteIntegrations({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Integration>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deleteIntegrations: mutation.mutateAsync,
    ...mutation,
  }
}
